import type { ValidationErrors } from '../../../utils/validation';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { supervisionEditPerformerSchema } from '../../../../common/validation/supervision';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { Button, ButtonContainer, Dropdown, DropdownSearch } from '../../elements/common';
import { Component } from '../../elements/Component';
import { DisplayMessage } from '../../elements/DisplayMessage';
import { FieldSet } from '../../elements/fieldset/FieldSet';
import { FormGroup } from '../../elements/forms/FormGroup';

interface IChangeServiceSupervisorProps {
    currentSupervisorId: string;
    reload: () => Promise<void>;
    serviceId: string;
    taskId: string;
}

interface IServiceSupervisor {
    employment: {
        organization: {
            id: string;
            name: string;
        };
        practitioner: {
            id: string;
            nameFamily: string;
        };
    };
    id: string;
}

interface IPerformer {
    performerOrganizationId: string | null;
    performerPractitionerId: string | null;
}

interface IChangeServiceSupervisorState {
    errors: ValidationErrors;
    message: string | null;
    performer: IPerformer;
    serviceSupervisors: IServiceSupervisor[];
}

export class ChangeServiceSupervisor extends Component<IChangeServiceSupervisorProps, IChangeServiceSupervisorState> {
    constructor(props: IChangeServiceSupervisorProps, context?: {}) {
        super(props, context);

        this.state = {
            performer: {
                performerOrganizationId: null,
                performerPractitionerId: null,
            },
            serviceSupervisors: [],
            errors: emptyValidationErrors,
            message: null,
        };
    }

    public render() {
        return (
            <FieldSet>
                <FormGroup hasErrors={this.state.errors.has('performerOrganizationId')}>
                    <DropdownSearch
                        error={this.state.errors.get('performerOrganizationId')}
                        loadOptions={async (input) => await this.getServiceSupervisors(input)}
                        onChange={async (value) => await this.handleChange('performerOrganizationId', value)}
                        onDoValidate={async () => await this.validateOne('performerOrganizationId')}
                        placeholder={translateKey('common.selectItem', translateKey('organization.organization'))}
                        value={this.state.performer.performerOrganizationId}
                    />
                </FormGroup>
                <FormGroup hasErrors={this.state.errors.has('performerPractitionerId')}>
                    <Dropdown
                        error={this.state.errors.get('performerPractitionerId')}
                        onChange={async (value) => await this.handleChange('performerPractitionerId', value)}
                        onDoValidate={async () => await this.validateOne('performerPractitionerId')}
                        placeholder={translateKey('common.selectItem', translateKey('practitioner.practitioner'))}
                        selectOptions={this.getServiceSupervisorPractitionersOptions()}
                        value={this.state.performer.performerPractitionerId}
                    />
                </FormGroup>
                {this.state.message && (
                    <DisplayMessage
                        message={this.state.message}
                        messageType="info"
                        onClose={async () => await this.setState({ message: null })}
                    />
                )}
                <ButtonContainer
                    buttons={[
                        <Button
                            color="secondary"
                            disabled={
                                !this.state.performer.performerOrganizationId ||
                                !this.state.performer.performerPractitionerId
                            }
                            onClick={async () => await this.saveServiceSupervisor()}
                        >
                            {translateKey('request.assign')}
                        </Button>,
                    ]}
                    noMargin
                />
            </FieldSet>
        );
    }

    private async handleChange<K extends keyof IPerformer>(name: K, value: IPerformer[K]) {
        await this.setState(({ performer }) => ({ performer: { ...performer, [name]: value } }));
        if (name === 'performerOrganizationId' && value) {
            await this.setState(({ performer }) => ({ performer: { ...performer, performerPractitionerId: null } }));
            const result = await fetchJson(
                `/api/serviceSupervisors?serviceId=${this.props.serviceId}&isActive=true&organizationId=${value}`,
                'GET'
            );
            await this.setState({ serviceSupervisors: result.json.items });
        }
    }

    private async validateOne(name: keyof IPerformer) {
        await this.setState(({ performer, errors }) => ({
            errors: updateValidationErrorsForOne(supervisionEditPerformerSchema, performer, errors, name),
        }));
    }

    private async getServiceSupervisors(input: string) {
        const result = await fetchJson(
            `/api/serviceSupervisors?serviceId=${this.props.serviceId}&isActive=true&searchOrganization=${input}&onlyOrganizationName=true&excludeDiagnosticReportPerformerOrganizationForSupervisionId=${this.props.taskId}`,
            'GET'
        );
        const options = result.json.items.map((serviceSupervisor: IServiceSupervisor) => ({
            value: serviceSupervisor.employment.organization.id,
            label: serviceSupervisor.employment.organization.name,
        }));

        return options;
    }

    private getServiceSupervisorPractitionersOptions() {
        const serviceSupervisors = this.state.serviceSupervisors.filter(
            (serviceSupervisor) => serviceSupervisor.employment.practitioner.id !== this.props.currentSupervisorId
        );

        return serviceSupervisors.map((serviceSupervisor: IServiceSupervisor) => ({
            value: serviceSupervisor.employment.practitioner.id,
            text: getDisplayName(serviceSupervisor.employment.practitioner),
        }));
    }

    private async saveServiceSupervisor() {
        await this.setState({ message: null });
        const { errors, item } = validate(supervisionEditPerformerSchema, this.state.performer);
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }
        await fetchJson(`/api/supervisions/${this.props.taskId}/updatePerformer`, 'POST', item);
        await this.setState({ message: translateKey('serviceProvider.changedSuccess') });
        await this.props.reload();
    }
}
